import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Clock, User, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { formatDuration } from '../../utils/ViewAsUserManager';
import { getStaffDisplayName } from '../../utils/staffDisplay';

/**
 * ViewAsUserBanner - Banner flutuante exibido quando staff está em modo "Visualizar como Usuário"
 * 
 * Este componente:
 * - Exibe informações do usuário sendo simulado
 * - Mostra timer de duração da sessão
 * - Fornece botão para retornar ao painel original
 * - Indica claramente que está em modo somente leitura
 */
const ViewAsUserBanner = () => {
    const { isViewingAsUser, viewAsUser, originalUser, stopViewAsUser } = useAuth();
    const [duration, setDuration] = useState(0);
    const [startTime] = useState(Date.now());

    // Atualizar timer a cada segundo
    useEffect(() => {
        if (!isViewingAsUser) return;

        const interval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            setDuration(elapsed);
        }, 1000);

        return () => clearInterval(interval);
    }, [isViewingAsUser, startTime]);

    const handleReturn = () => {
        const result = stopViewAsUser();
        if (result.success) {
            // Opcional: mostrar notificação de sucesso
            console.log(result.message);
        } else {
            alert(result.error);
        }
    };

    if (!isViewingAsUser) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -100, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="fixed top-0 left-0 right-0 z-[9999] pointer-events-none"
            >
                <div className="w-full px-4 pt-2">
                    <div className="pointer-events-auto max-w-7xl mx-auto">
                        <div className="relative overflow-hidden rounded-xl border border-orange-500/30 shadow-lg shadow-orange-500/10">
                            {/* Gradiente de fundo mais discreto */}
                            <div className="absolute inset-0 bg-gradient-to-r from-orange-600/90 via-red-600/90 to-orange-600/90" />

                            {/* Conteúdo compacto */}
                            <div className="relative backdrop-blur-md bg-black/20 px-4 py-2">
                                <div className="flex items-center justify-between gap-4">
                                    {/* Lado Esquerdo - Título e User info */}
                                    <div className="flex items-center gap-3 flex-1 overflow-hidden">
                                        <div className="flex items-center gap-2 px-2 py-1 bg-white/10 rounded-lg border border-white/10 shrink-0">
                                            <Eye className="h-4 w-4 text-white animate-pulse" />
                                            <span className="text-sm font-bold text-white whitespace-nowrap hidden sm:inline">MODO VISUALIZAÇÃO</span>
                                            <Badge className="bg-red-500/40 text-red-100 border-red-400/30 text-[10px] h-5 px-1.5 uppercase tracking-wider">
                                                Read-Only
                                            </Badge>
                                        </div>

                                        <div className="h-4 w-px bg-white/20 hidden md:block" />

                                        {/* Informações Resumidas */}
                                        <div className="flex items-center gap-4 text-sm overflow-hidden whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <User className="h-4 w-4 text-orange-200" />
                                                <span className="text-white font-medium truncate">
                                                    {viewAsUser?.name}
                                                </span>
                                            </div>

                                            <div className="hidden lg:flex items-center gap-2 text-orange-200/60">
                                                <Clock className="h-3.5 w-3.5" />
                                                <span className="font-mono text-xs">{formatDuration(duration)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Lado Direito - Ações */}
                                    <div className="flex items-center gap-3 shrink-0">
                                        <div className="text-right hidden xl:block">
                                            <p className="text-[10px] text-orange-200/70 leading-none">Logado como:</p>
                                            <p className="text-xs font-semibold text-white">{getStaffDisplayName(originalUser)}</p>
                                        </div>

                                        <Button
                                            onClick={handleReturn}
                                            variant="outline"
                                            size="sm"
                                            className="bg-white/10 hover:bg-white text-white hover:text-orange-600 border-white/20 hover:border-white font-bold transition-all px-3 h-8 text-xs gap-1.5"
                                        >
                                            <ArrowLeft className="h-3.5 w-3.5" />
                                            <span>Sair do Modo Simulação</span>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ViewAsUserBanner;
